import './Field.scss';
import { Component } from '@components/Component/Component.js';
import { takeUntil } from 'rxjs';
import { Label } from '@components/Component/TextBase/Label/Label.js';
import { ButtonsMixin } from '@components/Mixin/ButtonsMixin/ButtonsMixin.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { capitalizeFirstLetter } from '@components/Utility/index.js';
import { eventContextGet } from '@components/EventConfigManager/EventConfigManagerUtil.js';
import { ContextMenuMixin } from '@components/Mixin/ContextMenuMixin/ContextMenuMixin.js';
import { ENV } from '@components/Environment.js';
/**
 * Field component
 */
export class Field extends ContextMenuMixin(ButtonsMixin(Component)) {
    focusTelemetryMetadataGet(event) {
        if (event.target?.closest('button'))
            return null;
        return {
            column: this.columnName,
            table: this.record?.propertyGet('tableName')
        };
    }
    get columnName() { return this._columnName; }
    set columnName(value) {
        if (this._columnName !== value) {
            // this._columnName = value;
            this._propertySet('columnName', value);
            if (this.record) {
                // reset our record subscription for the new column name. 
                this.linkRecord(this.record);
            }
        }
    }
    get displayColumnName() { return this._displayColumnName; }
    set displayColumnName(value) {
        if (this._displayColumnName !== value) {
            this._propertySet('displayColumnName', value);
            if (this.record) {
                // reset our record subscription for the new column name. 
                this.linkRecord(this.record);
            }
        }
    }
    get displayValue() {
        if (typeof this._displayValue === 'undefined') {
            return this.strValue;
        }
        return this._displayValue;
    }
    set displayValue(value) {
        this._propertySet('displayValue', value);
        if (this.drawn) {
            this.renderValue();
        }
    }
    get showLabel() { return this._showLabel; }
    set showLabel(show) {
        this._propertySet('showLabel', show);
        if (show) {
            this.label.show();
        }
        else {
            this.label.hide();
        }
    }
    get labelPosition() { return this._labelPosition; }
    set labelPosition(value) {
        this._propertySet('labelPosition', value);
        if (this.drawn) {
            this.renderLabelPosition();
        }
    }
    get labelWidth() { return this._labelWidth; }
    set labelWidth(value) {
        this._propertySet('labelWidth', value);
        this.label.width = value;
    }
    get required() { return this._required || false; }
    set required(value) {
        this._propertySet('required', value);
        this.renderRequiredFlag();
    }
    get dataDict() { return this._dataDict; }
    set dataDict(dd) {
        if (this._dataDict === dd) {
            return; // same object, should be the same details. 
        }
        this._propertySet('dataDict', dd);
        this.updateDisabledState();
        this.renderRequiredFlag();
    }
    set maxLength(val) {
        val = Number(val) || 0;
        if (val < 1) {
            val = Infinity;
        }
        this._propertySet('maxLength', val);
    }
    get fieldLength() {
        if (this._maxLength) {
            return this._maxLength;
        }
        let fieldLen = Infinity;
        if (this._dataDict) {
            switch ((this.dataDict?.sqlType || '').toUpperCase()) {
                case 'SMALLINT':
                    return 5;
                case 'INT':
                    return 10;
                case 'BIGINT':
                    return 19;
                case 'MONEY':
                    return 19;
                case 'NUMERIC':
                case 'DECIMAL':
                    return Number(this._dataDict.field_len);
                default:
                    fieldLen = Number(this._dataDict.field_len);
                    if (fieldLen < 1) {
                        fieldLen = Infinity;
                    }
            }
        }
        return fieldLen;
    }
    get fieldDecimals() {
        let fieldLen = 0;
        if (this._dataDict) {
            fieldLen = Number(this._dataDict.field_dec);
        }
        return fieldLen;
    }
    get value() { return this._value; }
    set value(value) {
        if (!value) {
            // need to preserve false and 0 which are legitimate values but act "falsey"
            // but we want to turn other falsey values to empty string.
            if (typeof value === 'undefined') {
                value = '';
            }
            else if (value === null) {
                value = '';
            }
        }
        this._propertySet('value', value);
        if (this.drawn) {
            if (this.userChangeEventInProgress) {
                // many components like an input, the value looks correct from the user change so no point rendering it again
                return;
            }
            this.renderValue();
        }
    }
    get strValue() {
        return this._value.toString();
    }
    get inputValue() {
        return this.inputElement?.value;
    }
    get labelCaption() { return this._labelCaption || ''; }
    set labelCaption(value) {
        if (this._labelCaption !== value) {
            this._propertySet('labelCaption', value);
            this.renderLabelCaption();
        }
    }
    get labelTooltip() { return this._labelTooltip || ''; }
    set labelTooltip(value) {
        if (this._labelTooltip !== value) {
            this._propertySet('labelTooltip', value);
            this.renderLabelTooltip();
        }
    }
    get zoomTemplate() { return this._zoomTemplate || ''; }
    set zoomTemplate(value) {
        this._propertySet('zoomTemplate', value);
        this.label.zoomTemplate = value;
        if (this.record) {
            this.label.linkRecord(this.record);
        }
    }
    get zoomConditions() { return this._zoomConditions; }
    set zoomConditions(value) {
        this._propertySet('zoomConditions', value);
        this.label.zoomConditions = value;
    }
    constructor(id, rootElement, dependencies = {}) {
        super(id, rootElement, dependencies);
        this.componentName = 'Field';
        this.keyPressEventId = '';
        this._displayColumnName = '';
        /** Boolean. FALSE Does not add a label element to the input, so it will take up the full width of its container  */
        this._showLabel = true;
        this._labelPosition = '';
        /** Boolean. Must be set before draw. Makes input larger for forms like login screen.  */
        this.largeField = false; // TODO: make this property responsive to changes for the form designer maybe
        /** The field value */
        this._value = '';
        this.userChangeEventInProgress = false;
        this._columnName = id;
        this._enabledRecordStates = ['add', 'edit'];
        this.label = new Label(id + '_label', null, dependencies);
        this.label.formDesignerMode = false;
        this.label.propertySet('disableRecordCaptionKeyTokens', true);
        this.initializeButtons();
    }
    captionTypesSet() {
        this.captionTypes = {
            caption: 'labelCaption',
            tooltip: 'labelTooltip'
        };
    }
    initializeButtons() {
    }
    renderLabelCaption() {
        this.label.caption = this.labelCaption;
    }
    renderLabelTooltip() {
        this.label.tooltip = this.labelTooltip;
    }
    shouldRequire() {
        return this.required || this.dataDict?.mandatoryFlag === 'Y';
    }
    renderRequiredFlag() {
        if (this.drawn) {
            const required = this.shouldRequire();
            this.rootElement.classList.toggle('field-required', required);
        }
    }
    draw() {
        // components extending this will need to inherit some styles
        super.draw();
        this.rootElement.classList.add('s360Field');
        this.label.draw();
        // if (this._value) {
        // even if value is undefined, some components like "InputRange" need special styling for undefined.
        // also values like 0 are allowed but are falsey. 
        this.renderValue();
        // }
        if (this._labelPosition) {
            this.renderLabelPosition();
        }
        if (this.shouldRequire()) {
            this.renderRequiredFlag();
        }
        this.setupUserChangeEvent();
        // this.setupProgrammaticValueChange();
    }
    /**
     * This function should be extended by subclasses.
     * Use this function to setup event listener(s) using `registerEvent` which will call `handleUserChange` at the appropriate time for that type of component.
     */
    setupUserChangeEvent() {
        if (this.componentName !== 'Field') {
            console.warn(`Implement setupUserChangeEvent in ${this.componentName}.ts`);
        }
    }
    /**
     * This function handles a user change event. It is expected to be extended by most subclasses.
     * Determine the newValue by extracting it from your DOM element that the user interacted with (or from the event),
     * and finally call the userChange function to apply the value to the record and for a hook for form level code to block this event.
     * @param event
     * @protected
     */
    handleUserChange(event) {
        const oldValue = this._value;
        const newValue = this.inputElement.innerText;
        this.userChange(newValue, oldValue, event);
    }
    /**
     * Sanitizes the given value on user change
     *
     * @param {string|ValueType} newValue - The value to be sanitized.
     * @return {string|ValueType} - The sanitized value.
     */
    sanitizeValue(newValue) {
        if (newValue === '{"conditions":[]}') {
            return '';
        }
        return newValue;
    }
    triggerUserChange(newValue, event) {
        return this.userChange(newValue, this.value, event, true);
    }
    /**
     * Update value when user changes the input
     * @param newValue
     * @param oldValue
     * @param event - The input event
     * @param renderValueOnUserChange
     */
    userChange(newValue, oldValue, event, renderValueOnUserChange, additionalDisplayValues) {
        // this is where components with formatted values like dates or numbers can ensure that the entered value adheres to the properties of the component.
        const preSanitizedValue = newValue;
        newValue = this.sanitizeValue(newValue);
        if (newValue !== preSanitizedValue && typeof renderValueOnUserChange === 'undefined') {
            renderValueOnUserChange = true;
        }
        return this.trigger('userchange', {
            defaultEventHandler: this.defaultUserChangeEvent.bind(this),
            postEventHandler: this.afterUserChangeEvent.bind(this),
            eventBlockedHandler: () => {
                // event was blocked by a before('userchange') handler. 
                if (event) {
                    event.preventDefault();
                }
                this.value = oldValue;
            }
        }, newValue, oldValue, event, renderValueOnUserChange, additionalDisplayValues);
    }
    /**
     * Useful to extend in subclasses. This handler is only called after a user change occurs and the userchange event did not get blocked by component.before('userchange') type code.
     * @param newValue
     * @param oldValue
     * @param event
     * @param renderValueOnUserChange
     */
    defaultUserChangeEvent(newValue, oldValue, event, renderValueOnUserChange = false, additionalDisplayValues) {
        if (!renderValueOnUserChange) {
            this.userChangeEventInProgress = true;
        }
        this.value = newValue;
        this.userChangeEventInProgress = false;
        if (this.record) {
            this.record.set(this.columnName, newValue, this.id); // in form designer, when changing component type this can cause this.record to be undefined
            if (additionalDisplayValues) {
                Object.keys(additionalDisplayValues).forEach((key) => {
                    this.record?.set(key, additionalDisplayValues[key]);
                });
            }
            if (this.displayColumnName && !additionalDisplayValues?.[this.displayColumnName]) {
                this.record.set(this.displayColumnName, newValue, this.id);
            }
            if (this.record?.tableName
                && DIContainer.isSingletonRegistered(capitalizeFirstLetter(this.record.tableName))
                && !this.rootElement.classList.contains('cell-editor') // if its a grid editor, for now grid is handling the call to bizzlogic
            ) {
                const bizzClass = DIContainer.getInstance(capitalizeFirstLetter(this.record.tableName));
                const ctx = eventContextGet({
                    initiator: this,
                    record: this.record
                });
                bizzClass.userChange(ctx, this._columnName, newValue, oldValue);
            }
        }
    }
    afterUserChangeEvent(_eventReturnValue) {
        // hook for components like InputDateTime to use without taking up the .after handler that might need to be used by some external code. 
    }
    /**
     * Renders the `value` property for this input in the DOM
     * This function is called automatically once the Input has drawn.
     * @returns {Field} - This input instance for chaining calls
     */
    renderValue() {
        if (this.inputElement) {
            this.inputElement.innerText = this.displayValueGet();
        }
    }
    displayValueGet() {
        if ( /*this.displayColumnName && */this.displayValue) {
            return this.displayValue;
        }
        return typeof this.value === 'undefined' ? '' : this.strValue;
    }
    renderDisabledState(isDisabled) {
        super.renderDisabledState(isDisabled);
        if (this.inputElement) {
            // disabled attribute interferes with the form designer dragging operations. it will still get the css class to "look" disabled.
            if (this.formDesignerMode) {
                if (isDisabled) {
                    this.inputElement.classList.add('disabled');
                }
                else {
                    this.inputElement.classList.remove('disabled');
                }
            }
            else {
                if (isDisabled) {
                    this.inputElement.setAttribute('disabled', 'disabled');
                }
                else {
                    this.inputElement.removeAttribute('disabled');
                }
            }
        }
    }
    renderLabelPosition() {
        if (this.drawn) {
            // Create a regular expression to match classes that exigence it with 'label-pos-'
            const classRegex = /label-pos-\S+/;
            // Remove all classes that match the regex from this.rootElement
            this.rootElement.classList.forEach(className => {
                if (classRegex.test(className)) {
                    this.rootElement.classList.remove(className);
                }
            });
            // Remove all classes that match the regex from this.label.rootElement
            this.label.rootElement.classList.forEach(className => {
                if (classRegex.test(className)) {
                    this.label.rootElement.classList.remove(className);
                }
            });
            // If _labelPosition is not empty, add the class
            if (this._labelPosition !== '') {
                const className = `label-pos-${this._labelPosition}`;
                this.rootElement.classList.add(className);
                this.label.rootElement.classList.add(className);
            }
        }
    }
    focus() {
        this.inputElement?.focus();
    }
    blur() {
        this.inputElement?.blur();
    }
    /**
     * Link the field to a record by its columnName. This syncs the field `value` with the record.data[columnName].
     * @param record - The record to link to
     */
    linkRecord(record) {
        super.linkRecord(record);
        this.dataDict = record.dataDictByColumnGet(this.columnName);
        this.setupRecordSubscription(record);
        if (this.zoomTemplate) {
            this.label.linkRecord(record);
        }
    }
    unlinkRecord(keepState = false) {
        if (this.recordDisplayValueSubscription) {
            this.recordDisplayValueSubscription.unsubscribe();
            this.recordDisplayValueSubscription = undefined;
        }
        if (this.recordLoadSubscription) {
            this.recordLoadSubscription.unsubscribe();
            this.recordLoadSubscription = undefined;
        }
        super.unlinkRecord(keepState);
    }
    /*protected shouldDisable(): boolean {
        const parent = super.shouldDisable();
        return parent || this._editLevel > Number(ENV('permLevel'));
    }*/
    shouldDisable() {
        const shouldDisable = super.shouldDisable(); // disabled property and enabledRecordStates checks.
        if (!shouldDisable && this._dataDict && this._dataDict.permEdit !== 'Y') {
            // check if they have permission in the datadict for this field
            // perm edit includes edit level and edit conditions for this specific field.
            return true;
        }
        const record = this.record;
        if (!record || shouldDisable) {
            return shouldDisable;
        }
        if (record.tableName && DIContainer.isSingletonRegistered(capitalizeFirstLetter(record.tableName))) {
            const bizzClass = DIContainer.getInstance(capitalizeFirstLetter(record.tableName));
            if (!bizzClass.isEditable(record, this.columnName)) {
                return true;
            }
        }
        else {
            const bizzClass = DIContainer.getInstance('RecordLogic');
            if (!bizzClass.isEditable(record, this.columnName)) {
                return true;
            }
        }
        return false;
    }
    setupRecordSubscription(record) {
        this.recordSubscription = record.get$(this.columnName, { ignoreSource: [this.id], includeDetail: true, includeInitial: true })
            .pipe(takeUntil(this.destroy$))
            .subscribe(({ value, source }) => {
            this.value = value;
            // if the value changes, we should clear the displayColumn as well, it would be expected that the code setting the value would also set the new display value afterwards if applicable
            if (this._displayColumnName && source !== 'load' && this._displayColumnName !== this._columnName) {
                record.set(this._displayColumnName, value);
            }
        });
        this.setupRecordDisplayValueSubscription(record);
        this.setupRecordLoadSubscription(record);
        this.setupRecordDisabledStateSubscription(record);
    }
    setupRecordDisabledStateSubscription(record) {
        if (this.recordDisabledStateSubscription) {
            this.recordDisabledStateSubscription.unsubscribe();
        }
        this.recordDisabledStateSubscription = record.refreshDisabledState$.subscribe(() => {
            this.updateDisabledState();
        });
    }
    setupRecordLoadSubscription(record) {
        // if (this.recordLoadSubscription) {
        // 	this.recordLoadSubscription.unsubscribe();
        // 	this.recordLoadSubscription = undefined;
        // }
        //
        // this.recordLoadSubscription = record.refreshDisabledState$.subscribe(() => {
        // 	this.updateDisabledState();
        // });
    }
    setupRecordDisplayValueSubscription(record) {
        if (this.recordDisplayValueSubscription) {
            this.recordDisplayValueSubscription.unsubscribe();
            this.recordDisplayValueSubscription = undefined;
        }
        if (this._displayColumnName) {
            this.recordDisplayValueSubscription = record.get$(this._displayColumnName, { ignoreSource: [this.id] })
                .pipe(takeUntil(this.destroy$))
                .subscribe((value) => {
                this.displayValue = value;
            });
        }
    }
    formDesignerPropertiesGet(properties) {
        properties = super.formDesignerPropertiesGet(properties);
        if (!this.compositeChild) {
            properties.componentName.componentType = 'Select';
        }
        properties.enabledRecordStates.saveFlag = () => {
            // return true if this._enabledRecordStates contains any more or less values than the default (['add', 'edit' ]). _enabledRecordStates is type []|undefined
            const defaultStates = ['add', 'edit'];
            if (!this._enabledRecordStates) {
                this._enabledRecordStates = [];
                return true;
            }
            if (this._enabledRecordStates.length !== defaultStates.length) {
                return true;
            }
            // Check if all elements in _enabledRecordStates exist in defaultStates
            const allExist = this._enabledRecordStates.every(state => defaultStates.includes(state));
            // Return true if there's a difference
            return !allExist;
        };
        properties.columnName = ({
            id: 'columnName',
            value: this.columnName,
            componentType: 'Input',
            componentProperties: {
                disabled: !!this.compositeChild
            },
            captionKey: 'formbuilder_columnname',
            propertyGroup: 'data',
            propertyIndex: 2,
            saveFlag: false
        });
        let defaultValue = '';
        if (this.record) {
            const q360FormRecordValue = this.record.q360formrecordvalue?.find((o) => {
                return (o.columnname || '').toLowerCase() === this.columnName;
            });
            if (q360FormRecordValue) {
                defaultValue = q360FormRecordValue.defaultvalue;
            }
        }
        properties.defaultValue = ({
            id: 'defaultValue',
            value: defaultValue,
            componentType: 'Input',
            componentProperties: {
                autoCompleteTokens: Object.keys(ENV('tokens')).map((s) => ({ value: s.replace('{{', '').replace('}}', '') }))
            },
            captionKey: 'formbuilder_field_defaultvalue',
            propertyGroup: 'data',
            propertyIndex: 2.02,
            saveFlag: false
        });
        properties.labelPosition = ({
            id: 'labelPosition',
            value: this._labelPosition,
            componentType: 'Select',
            componentProperties: {
                staticOptionData: [
                    { value: '', display: this.CaptionManager.getCaption('formbuilder_dynamic_caption') },
                    { value: 'left', display: this.CaptionManager.getCaption('formbuilder_left_caption') },
                    { value: 'top', display: this.CaptionManager.getCaption('formbuilder_top_caption') }
                ]
            },
            captionKey: 'formbuilder_field_labelposition',
            propertyGroup: 'caption',
            propertyIndex: 5,
            saveFlag: () => !!this._labelPosition
        });
        properties.showLabel = ({
            id: 'showLabel',
            value: this._showLabel,
            componentType: 'InputCheckbox',
            componentProperties: {
                checkedValue: true,
                unCheckedValue: false
            },
            captionKey: 'formbuilder_field_showlabel',
            propertyGroup: 'caption',
            propertyIndex: 6,
            saveFlag: () => !this._showLabel
        });
        properties.labelWidth = ({
            id: 'labelWidth',
            value: this._labelWidth ? this._labelWidth : '115px',
            componentType: 'Input',
            componentProperties: {},
            captionKey: 'formbuilder_field_labelwidth',
            propertyGroup: 'caption',
            propertyIndex: 6,
            saveFlag: () => !!this._labelWidth && this._labelWidth !== '115' && this._labelWidth !== '115px'
        });
        if (this.parentComponentGet() && Reflect.has(this.parentComponentGet(), 'dbGridName')) {
            properties.width = ({
                id: 'width',
                value: this._width,
                componentType: 'Input',
                componentProperties: {},
                captionKey: 'formbuilder_width',
                propertyGroup: 'layout',
                saveFlag: () => !!this._width
            });
        }
        properties.zoomTemplate = {
            id: 'zoomTemplate',
            value: this.zoomTemplate,
            componentType: 'Input',
            componentProperties: {},
            captionKey: 'formbuilder_zoomtemplate',
            propertyGroup: 'caption',
            propertyIndex: 7,
            saveFlag: !!this.zoomTemplate
        };
        properties.zoomConditions = {
            id: 'zoomConditions',
            value: this.zoomConditions,
            componentType: 'ConditionsEditor',
            componentProperties: {
                labelPosition: 'top'
            },
            captionKey: 'formbuilder_zoomconditions',
            propertyGroup: 'caption',
            propertyIndex: 8,
            saveFlag: !!this.zoomConditions
        };
        properties.disabled = ({
            id: 'disabled',
            value: this.disabled,
            componentType: 'InputCheckbox',
            componentProperties: {
                checkedValue: true,
                unCheckedValue: false
            },
            captionKey: 'formbuilder_disabled',
            propertyGroup: '',
            saveFlag: () => this.disabled
        });
        properties.required = {
            id: 'required',
            value: this.required,
            componentType: 'InputCheckbox',
            componentProperties: {
                checkedValue: true,
                unCheckedValue: false
            },
            captionKey: 'formbuilder_field_required',
            propertyGroup: '',
            saveFlag: () => this.required
        };
        // add datadict fields just for display purposes, not editable. 
        properties.dataDict = ({
            id: 'dataDict',
            value: this._dataDict,
            componentType: 'Input',
            componentProperties: {
                hidden: true
            },
            captionKey: '',
            propertyGroup: '',
            propertyIndex: 0,
            refreshPanelOnChange: true,
            saveFlag: false
        });
        if (this._dataDict) {
            properties['dataDict.sqlType'] = {
                id: 'dataDict.sqlType',
                value: this._dataDict?.sqlType,
                componentType: 'Input',
                componentProperties: {
                    disabled: true
                },
                captionKey: 'formbuilder_datadict_sqltype',
                propertyGroup: 'datadict',
                propertyIndex: 1,
                saveFlag: () => false
            };
            properties['dataDict.field_len'] = {
                id: 'dataDict.field_len',
                value: this._dataDict?.field_len,
                componentType: 'Input',
                componentProperties: {
                    disabled: true
                },
                captionKey: 'formbuilder_datadict_field_len',
                propertyGroup: 'datadict',
                propertyIndex: 2,
                saveFlag: () => false
            };
            properties['dataDict.mandatoryFlag'] = {
                id: 'dataDict.mandatoryFlag',
                value: this._dataDict?.mandatoryFlag,
                componentType: 'InputCheckbox',
                componentProperties: {
                    disabled: true
                },
                captionKey: 'formbuilder_datadict_mandatoryflag',
                propertyGroup: 'datadict',
                propertyIndex: 3,
                saveFlag: () => false
            };
            properties['dataDict.editCondition'] = {
                id: 'dataDict.editCondition',
                value: this._dataDict?.editCondition,
                componentType: 'ConditionsEditor',
                componentProperties: {
                    disabled: true,
                    labelPosition: 'top'
                },
                captionKey: 'formbuilder_datadict_editcondition',
                propertyGroup: 'datadict',
                propertyIndex: 4,
                saveFlag: () => false
            };
        }
        return properties;
    }
    template() {
        this.labelTemplate();
        if (this.largeField) {
            this.rootElement.classList.add('largeField');
        }
        this.fieldInputWrapper = document.createElement('span');
        this.fieldInputWrapper.classList.add('fieldInputWrapper');
        this.loadingThrobberRoot = this.fieldInputWrapper;
        this.inputTemplate();
        this.rootElement.appendChild(this.fieldInputWrapper);
        return '';
    }
    buttonContainerCreate() {
        const buttonContainer = document.createElement('div');
        buttonContainer.classList.add('button-container');
        this.buttonContainers.set('', buttonContainer); // default container id is blank so that components with only one button container dont have to worry about the id.
        this.fieldInputWrapper?.appendChild(buttonContainer);
    }
    /**
     * Fields primarily consist of label and input. This function needs to insert the label rootElement wherever it needs to be.
     * @protected
     */
    labelTemplate() {
        this.label.rootElement = document.createElement('span');
        // if (this.showLabel) {
        this.rootElement.appendChild(this.label.rootElement);
        // }
    }
    /**
     * inputElement specific manipulations / alternatives should be setup here.
     * @protected
     */
    inputTemplate() {
        this.inputElement = document.createElement('span');
        this.inputElement.classList.add('fieldInput');
        this.fieldInputWrapper?.appendChild(this.inputElement);
    }
    getContextMenuItems(ctx) {
        const items = [];
        const conditionsManager = DIContainer.getInstance('ConditionsManager');
        if (this.captionKey
            && conditionsManager.checkPerm('user_admin')
            && (ctx.event?.target).closest('.s360Label') === this.label.rootElement) {
            items.push({
                caption: 'Edit Label',
                onClick: () => {
                    this.editLabelCaption();
                }
            });
        }
        if (this.record?.filterModule && this.columnName && this.record.filterExcludeList.indexOf(this.columnName) === -1) {
            items.push({
                caption: 'Save Filter',
                onClick: () => {
                    this.trigger('saveFilter', {
                        defaultEventHandler: () => {
                            this.handleProgress(0);
                            this.record.saveFilters(this.columnName).subscribe(() => this.handleProgress(100));
                        }
                    });
                }
            });
        }
        return items;
    }
    editLabelCaption() {
        DIContainer.resolveLazy('CaptionKeyEditModal')
            .subscribe((modal) => {
            modal.open({
                captionKey: this.captionKey,
                captionTypes: { ...{ caption: 'caption' }, ...this.captionTypes }
            }).subscribe(() => {
                this.captionsLoad();
            });
        });
    }
    destroy() {
        // remove reference to global variable
        delete this._dataDict;
        this.recordDisabledStateSubscription?.unsubscribe();
        this.recordDisabledStateSubscription = undefined;
        this.recordLoadSubscription?.unsubscribe();
        this.recordLoadSubscription = undefined;
        super.destroy();
    }
}
Field.VERSION = '$Revision: 112183 $';
